import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const MOBILE_MAX_WIDTH = 767;
const SCROLL_TOP_THRESHOLD = 8;

function isMobileViewport(): boolean {
  return window.innerWidth <= MOBILE_MAX_WIDTH;
}

const Header = () => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileViewport());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY <= SCROLL_TOP_THRESHOLD);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsAtTop(true);
  }, [location.pathname]);

  const hideOnMobile = isMobile && !isAtTop;
  const opaqueOnDesktop = !isMobile && !isAtTop;

  return (
    <HeaderContainer>
    <header
      className={`header-bar shadow-md fixed w-full top-0 z-10${hideOnMobile ? ' header-bar--hidden' : ''}${opaqueOnDesktop ? ' header-bar--opaque' : ''}`}
    >
      <nav className="container mx-auto px-6 py-4">
        <div className="grid grid-cols-3 items-center">
          <Link to="/" className="header-brand-link">
            <img
              src="/img/logo_512.png"
              alt="Vioo logo"
              className="h-10 w-10 rounded-lg object-cover"
            />
            <h1 className="header-brand-title">Vioo VIP</h1>
          </Link>
          <div className="header-nav-center">
            <Link to="/pricing" className="header-nav-link">
              Pricing
            </Link>
            <Link to="/contact-us" className="header-nav-link">
              Contact
            </Link>
          </div>
          <div className="flex justify-end">
            <Link to="/login" className="header-sign-in-btn">
              Sign in
            </Link>
          </div>
        </div>
      </nav>
    </header>
    </HeaderContainer>
  );
};

const HeaderContainer = styled.header`
  margin-bottom: 4rem;

  .header-bar {
    transition:
      transform 0.25s ease,
      background-color 0.25s ease,
      box-shadow 0.25s ease;
  }

  @media (min-width: ${MOBILE_MAX_WIDTH + 1}px) {
    .header-bar.header-bar--opaque {
      background-color: rgba(15, 23, 42, 0.96);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }
  }

  @media (max-width: ${MOBILE_MAX_WIDTH}px) {
    .header-bar.header-bar--hidden {
      transform: translateY(-100%);
      pointer-events: none;
    }

    .header-brand-title {
      display: none;
    }
  }

  h1 {
    font-size: 1.5rem;
    font-weight: bold;
    color: #CCC;
    margin: 0;
  }

  img {
    display: block;
  }

  .header-brand-link {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    color: inherit;
    text-decoration: none;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .header-brand-link:hover {
    transform: translateY(-2px);
    opacity: 0.92;
  }

  .header-brand-link:hover h1 {
    color: #e2e8f0;
  }

  .header-brand-link img {
    width: 2.5rem;
    height: 2.5rem;
  }

  .header-nav-center {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
  }

  @media (min-width: 640px) {
    .header-nav-center {
      gap: 1.5rem;
    }
  }

  .header-nav-link {
    font-family: "Roboto", sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: #cbd5e1;
    text-decoration: none;
    transition: color 0.2s ease, transform 0.2s ease;
  }

  .header-nav-link:hover {
    color: #22d3ee;
    transform: translateY(-2px);
  }

  .header-sign-in-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    border: 1px solid #818cf8;
    border-radius: 0.5rem;
    font-family: "Roboto", sans-serif;
    font-size: 0.9375rem;
    font-weight: 500;
    line-height: 1.5;
    color: #ffffff;
    background: transparent;
    text-decoration: none;
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  }

  .header-sign-in-btn:hover {
    color: #ffffff;
    background: #06b6d4;
    border-color: #06b6d4;
    transform: translateY(-2px);
  }

  .header-sign-in-btn:active {
    background: #0e7490;
    border-color: #0e7490;
    transform: translateY(0);
  }

  a {
    color: #CCC;
    text-decoration: none;
    transition: transform 0.2s;

    &:hover {
      transform: translateY(-3px);
    }

    img {
      width: 24px;
      height: 24px;
    }
  }
`;

export default Header; 